import * as Sentry from "@sentry/react";

import { logger } from "@/lib/logger";

// Header / query-param name fragments that carry client IPs or user identity.
const PII_KEY_DENYLIST = ["forwarded", "-ip", "remote-", "via", "-user"];

/**
 * Initialize Sentry error monitoring (BOIE-94).
 *
 * No-op when `VITE_SENTRY_DSN` is unset — the SDK is never initialized, so
 * nothing is captured or sent. That keeps it inert in local dev and any build
 * without a DSN; it activates automatically once the DSN env var is present.
 * Once initialized, Sentry auto-captures unhandled errors and promise
 * rejections; route/render errors are reported explicitly from the
 * `ErrorBoundary` (React Router intercepts those before they reach window).
 */
export function initSentry(): void {
  // Trim so a blank/whitespace DSN (e.g. the Docker build's empty ARG default)
  // stays inert instead of initializing the SDK with junk.
  const dsn = import.meta.env.VITE_SENTRY_DSN?.trim();
  if (!dsn) {
    return;
  }

  // Fail-soft: a malformed DSN must not break app boot (Sentry is here to watch
  // the app, not take it down). Mirrors the backend's guarded init.
  try {
    Sentry.init({
      dsn,
      // `||` (not `??`) so an empty-string env var — the Docker ARG default —
      // also falls back to Vite's MODE rather than sending an empty env tag.
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT?.trim() || import.meta.env.MODE,
      release: import.meta.env.VITE_APP_VERSION,
      // Errors are the priority; leave performance tracing off by default.
      tracesSampleRate: 0,
      // Avoid shipping PII. SDK v11 replaced `sendDefaultPii: false` with this
      // granular option and made its defaults permissive (IP inference, cookies,
      // bodies), so pin the v10 baseline from Sentry's migration guide.
      dataCollection: {
        userInfo: false,
        cookies: false,
        httpHeaders: {
          request: { deny: PII_KEY_DENYLIST },
          response: { deny: PII_KEY_DENYLIST },
        },
        httpBodies: [],
        urlQueryParams: { deny: PII_KEY_DENYLIST },
        genAI: { inputs: false, outputs: false },
        databaseQueryData: false,
        queues: false,
        graphQL: { document: false, variables: false },
      },
    });
  } catch (error) {
    logger.warn("[sentry] init failed; error monitoring disabled", error);
  }
}
